import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ICourse, IGetSteps } from '../../../models/courses.model';
import { Descriptions, Select } from 'antd';
import Loader from '../../Loader';
import Button from '../../Button';
import Radio from '../../Radio';
import useAuth from '../../../hooks/useAuth';
import useNotification from '../../../hooks/useNotification';
import useAxios from '../../../hooks/useAxios';
import { getSteps } from '../../../api/courses.api';
import ErrorResponse from '../../ErrorResponse';
import SelectEduStep from '../../SelectEduStep';

const lectureCountList = [
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 5, value: 5 }
];

const periodCountList = [
  { label: 1, value: 1 },
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 4, value: 4 },
];

interface IProps {
  course?: ICourse;
  submit: (req: ICourse) => void;
};

const ProgramCourseEditor: FC<IProps> = ({
  course,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSteps>();  

  const [eduStepCode, setEduStepCode] = useState<string>();
  const [level, setLevel] = useState<string>();
  const [levels, setLevels] = useState<any>([]);
  const [step, setStep] = useState<string>();
  const [steps, setSteps] = useState<any>([]);
  const [name, setName] = useState('');
  const [lectureCount, setLectureCount] = useState<any>();
  const [periodCount, setPeriodCount] = useState<any>();
  const [classTimeUnit, setClassTimeUnit] = useState(50);
  const [onlineFee, setOnlineFee] = useState('');
  const [alexTuition, setAlexTuition] = useState('');
  const [isInUse, setIsInUse] = useState(false);

  useEffect(() => {
    execute(getSteps());
  }, []);

  useEffect(() => {
    if (res) {
      const newLevels: any = [];
      res.data?.forEach(level => {
        newLevels.push({
          label: level.courseName,
          value: level.courseCode,
          eduStepCode: level.eduStepCode,
        });
      });
      setLevels(newLevels);
    }
  }, [res]);

  useEffect(() => {
    if (course && res) {
      res.data?.forEach((level: any) => {
        level.step?.forEach((step: any) => {
          if (step.courseCode === course.courseCode) {
            onSetLevel(level.courseCode);
            setStep(step.courseCode);
          }
        });
      });

      setEduStepCode(course.eduStepCode);
      setName(course.courseDetailName);
      setLectureCount(course.weekLectureCount);
      setPeriodCount(course.dayLessonCount);
      setClassTimeUnit(course.lessonTime);
      setOnlineFee(String(course.onlineTuition));
      setAlexTuition(String(course.alexTuition));
      setIsInUse(course.useYn === 'Y');
    }
  }, [course, res]);

  const onSetLevel = (value: any) => {
    const level = res?.data?.find(item => item.courseCode === value);
    if (level) {
      const newSteps: any = [];
      level.step?.forEach(item => {
        newSteps.push({
          label: item.courseName,
          value: item.courseCode
        });
      });
      setSteps(newSteps);
    }

    setStep(undefined);
    setLevel(value);
  }

  const onSubmit = async () => {
    if (!eduStepCode) {
      return infoNotification(t('program.course.1'));
    }
    if (!level) {
      return infoNotification(t('program.course.message1'));
    }
    if (!step) {
      return infoNotification(t('program.course.message2'));
    }
    if (!name) {
      return infoNotification(t('program.course.message3'));
    }
    if (!lectureCount) {
      return infoNotification(t('program.course.message4'));
    }
    if (!classTimeUnit) {
      return infoNotification(t('program.course.message5'));
    }
    if (!onlineFee || Number(onlineFee) < 0) {
      return infoNotification(t('program.course.message6'));
    }
    if (!alexTuition || Number(alexTuition) < 0) {
      return infoNotification(t('program.course.message7'));
    }

    submit({
      eduStepCode,
      courseCode: step,
      courseDetailName: name,
      weekLectureCount: lectureCount,
      dayLessonCount: periodCount,
      lessonTime: classTimeUnit,
      onlineTuition: Number(onlineFee),
      alexTuition: Number(alexTuition),
      // R = 정규과정
      curriculumType: 'R',
      useYn: isInUse ? 'Y' : 'N'
    })
  }

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <div className='comm-table__area rowType'>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label={t('common.label.1')} span={2}>
            <SelectEduStep
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                setLevel(undefined);
                setStep(undefined);
              }}
              disabled={!!course}
              width={210}
              noDefault
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column13')}>
            <div className="w-210p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={level}
                onChange={value => onSetLevel(value)}
                disabled={!!course || !eduStepCode}
              >
                {
                  levels.filter((item: any) => item.eduStepCode === eduStepCode).map((item: any) => {
                    return (
                      <Select.Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Select.Option>
                    )
                  })
                }
              </Select>                
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.2')}>
            <div className="w-210p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={step}
                onChange={value => setStep(value)}
                disabled={!level || !!course}
              >
                {
                  steps.map((item: any) => {
                    return (
                      <Select.Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Select.Option>
                    )
                  })
                }
              </Select>                
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column4')} span={2}>
            <input
              type="text"
              className="form-control input-text"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column6')}>
            <div className="flex" >
              <div className="v-line">{t('program.course.text1')}&nbsp;</div>
              <div className="w-100p">
                <Select
                  className="w-100"
                  value={lectureCount}
                  onChange={value => setLectureCount(value)}
                  disabled={!!course}
                >
                  {
                    lectureCountList?.map((item: any) => {
                      return (
                        <Select.Option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </Select.Option>
                      )
                    })
                  }
                </Select>
              </div>
              <div className="v-line">&nbsp;{t('program.course.text2')}</div>
            </div>          
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column14')}>
            <div className="flex">
              <div className="v-line">{t('program.course.text3')}&nbsp;</div>
              <div className="w-100p">
                <Select
                  className="w-100"
                  value={periodCount}
                  onChange={value => setPeriodCount(value)}
                  disabled={!!course}
                >
                  {
                    periodCountList?.map((item: any) => {
                      return (
                        <Select.Option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </Select.Option>
                      )
                    })
                  }
                </Select>                  
              </div>
              <div className="v-line">&nbsp;{t('program.course.text4')}</div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column15')}>
            <div className="flex">
              <div className="v-line">{t('program.course.text5')}&nbsp;</div>
              <div className="w-100p">
                <input
                  type="text"
                  className="form-control input-text"
                  value={classTimeUnit}
                  readOnly
                />
              </div>
              <div className="v-line">&nbsp;{t('program.course.text6')}</div>
            </div>          
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column16')}>
            <div className="flex">
              <div className="w-100p">
                <input
                  type="number"
                  className="form-control input-text"
                  value={onlineFee}
                  onChange={e => setOnlineFee(e.target.value)}
                />
              </div>
              <div className="v-line">&nbsp;{t('program.course.text7')}</div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column17')} span={2}>
            <div className="flex">
              <div className="w-100p">
                <input
                  type="number"
                  className="form-control input-text"
                  value={alexTuition}
                  onChange={e => setAlexTuition(e.target.value)}
                />
              </div>
              <div className="v-line">&nbsp;{t('program.course.text7')}</div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('program.course.column11')}>
            <div className="radio-wrapper">
              <Radio
                checked={isInUse}
                onChange={() => setIsInUse(true)}
                text={t('common.option.yes')}
              />
              <Radio
                checked={!isInUse}
                onChange={() => setIsInUse(false)}
                text={t('common.option.no')}
              />
            </div>
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/program/course${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </>
  )
}

export default ProgramCourseEditor;
